import axios, { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { getApiBaseUrl } from '../utils/urlConfig';
import { DEBUG_CONNECTIVITY, DEBUG_LOGS, DEBUG_VERBOSE_API } from '../utils/debugFlags';
import StorageService from './storage';
import { LogoutCoordinator } from './logoutCoordinator';
import { notifyDisclaimerRequired } from './disclaimerService';
import { coordinatedRefresh } from './refreshCoordinator';
import { beginApiRequest, endApiRequest } from './apiActivity';

// Extend axios config to include retry flag and optional skip-auth-failure (e.g. background polling)
interface ExtendedAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
  _cookieSettleAttempts?: number;
  _skipAuthFailure?: boolean;
}

export const API_BASE_URL = getApiBaseUrl();

const supportsAbortSignalTimeout =
  typeof AbortSignal !== 'undefined' &&
  typeof (AbortSignal as unknown as { timeout?: unknown }).timeout === 'function';

const createTimeoutSignal = (timeoutMs: number): AbortSignal => {
  if (supportsAbortSignalTimeout) {
    return (AbortSignal as unknown as { timeout: (ms: number) => AbortSignal }).timeout(timeoutMs);
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  if (typeof controller.signal.addEventListener === 'function') {
    controller.signal.addEventListener(
      'abort',
      () => clearTimeout(timeoutId),
      { once: true }
    );
  }

  return controller.signal;
};

/** Ten minutes. Data analysis is bounded by dataset size, not by a model. */
const DATA_ANALYSIS_TIMEOUT_MS = 600000;

/**
 * Four minutes, the ceiling for a synchronous model call.
 *
 * Deliberately under the nginx `/api/` `proxy_read_timeout` (300s): if the
 * client gave up *after* nginx did, a pathologically slow model would surface
 * as a confusing 504 HTML page instead of a clean client-side error.
 */
const AI_CALL_TIMEOUT_MS = 240000;

type SlowEndpoint = {
  /** Shown in the debug log when a rule fires. */
  readonly name: string;
  readonly timeoutMs: number;
  readonly matches: (url: string, method: string) => boolean;
};

/**
 * Requests whose work legitimately outlasts the 10s default.
 *
 * A table rather than three near-identical `if` blocks. The blocks were copied
 * from one another as each feature hit the ceiling, and copy-paste in this file
 * is exactly how the doubled `/api` prefix spread — a new AI feature should be
 * one row here, not another paragraph of duplicated logic.
 *
 * A rule only ever RAISES the timeout, and never below what a caller passed
 * explicitly, so overlapping rules resolve to the longest — same behaviour the
 * separate blocks had.
 */
const SLOW_ENDPOINTS: readonly SlowEndpoint[] = [
  {
    // Merged dataframes of hundreds of MB and millions of rows.
    name: 'data-analysis',
    timeoutMs: DATA_ANALYSIS_TIMEOUT_MS,
    matches: (url) => url.includes('/data-analysis/'),
  },
  {
    // Synchronous LLM calls: the Stage-1 assessor (initial + one retry), and
    // the Stage-2 generators when Celery is down and they run inline.
    // `/geriatric-assess/` = encounter Stage-1;
    // `/geriatric-assessments/` = edit + `/approve/` Stage-2 dispatch.
    name: 'geriatric-pipeline',
    timeoutMs: AI_CALL_TIMEOUT_MS,
    matches: (url) =>
      url.includes('/geriatric-assess/') || url.includes('/geriatric-assessments/'),
  },
  {
    // Creating a run extracts the facts (initial + one retry, extract.py).
    // POST only — the GET on this same URL lists runs and should stay snappy.
    name: 'cga-docgen-extract',
    timeoutMs: AI_CALL_TIMEOUT_MS,
    matches: (url, method) => method === 'post' && /\/cga-docgen\/runs\/$/.test(url),
  },
  {
    // Generating one field: up to MAX_REGENERATIONS + 1 = 3 attempts
    // (generate.py).
    name: 'cga-docgen-write',
    timeoutMs: AI_CALL_TIMEOUT_MS,
    matches: (url) => url.includes('/cga-docgen/runs/') && url.includes('/write/'),
  },
] as const;

/** Raise this request's timeout if any SLOW_ENDPOINTS rule claims it. */
const applySlowEndpointTimeout = (config: InternalAxiosRequestConfig): void => {
  const url = config.url || '';
  const method = (config.method || '').toLowerCase();
  for (const rule of SLOW_ENDPOINTS) {
    if (rule.matches(url, method) && (!config.timeout || config.timeout < rule.timeoutMs)) {
      config.timeout = rule.timeoutMs;
      if (DEBUG_VERBOSE_API) {
        console.log(`[REQUEST] timeout raised to ${rule.timeoutMs}ms by rule "${rule.name}"`);
      }
    }
  }
};

class ApiService {
  private api: AxiosInstance;
  private originalConsoleError = console.error;
  private originalConsoleWarn = console.warn;
  private originalConsoleLog = console.log;
  private originalXMLHttpRequest = XMLHttpRequest;
  private originalWindowError = window.onerror;

  // Simplified console suppression - no longer needed with new silent request method
  private suppressAllAuthConsoleOutput() {
    // Legacy method - now handled directly in silentAuthRequest
  }

  private restoreAllConsoleOutput() {
    // Legacy method - now handled directly in silentAuthRequest  
  }

  private shouldAttemptSessionRefresh(): boolean {
    // If a logout is already in flight, do NOT attempt to refresh — the user's
    // auth state is being torn down and any in-flight 401 should reject silently
    // rather than queue another refresh / logout cycle.
    // (See logoutCoordinator.ts for why this is critical to break the
    //  notes/labels/folders/disclaimer/feature-enablement 401 storm.)
    if (LogoutCoordinator.active) {
      return false;
    }

    // HttpOnly cookies are not readable from JS. Use confirmed auth state as the refresh gate
    // so startup validation does not fan out into repeated refresh attempts.
    const authState = window.store?.getState?.().auth;
    if (authState) {
      return (
        !!authState.isAuthenticated &&
        !!authState.user &&
        (authState.validationState === 'valid' || authState.validationState === 'failed')
      );
    }

    return !!StorageService.getUser();
  }

  private getAuthFailureReason(errorLike: any, fallback = 'Your session has expired. Please log in again.'): string {
    if (errorLike?.message?.includes?.('Refresh token missing')) {
      return 'Your session has expired. Please log in again.';
    }

    if (errorLike?.message?.includes?.('timeout') || errorLike?.name === 'AbortError') {
      return 'Server connection timeout. Please try again.';
    }

    return (
      errorLike?.response?.data?.error ||
      errorLike?.response?.data?.detail ||
      errorLike?.message ||
      fallback
    );
  }

  private async refreshSession(): Promise<void> {
    // Delegate to the shared coordinator so a refresh triggered here can never
    // race a refresh from authInterceptor.ts / tokenRefresh.ts against the
    // rotating /auth/refresh/ endpoint. coordinatedRefresh() already dedupes
    // concurrent callers app-wide, so we no longer keep a local refreshPromise.
    const result = await coordinatedRefresh();
    if (!result.ok) {
      const error = new Error(result.data?.error || result.data?.detail || `HTTP ${result.status}`);
      (error as any).response = { status: result.status, data: result.data };
      // Playbook §10: "Never treat a transient refresh failure (network/5xx) as
      // an auth failure. Only an explicit 401/403 should clear the session."
      // status 0 = network error (offline, dropped connection); 5xx = server
      // hiccup. Neither means the session was rejected, so callers must NOT log
      // the user out on them — see the guards on handleAuthFailure below.
      (error as any).isTransientRefreshFailure = result.status === 0 || result.status >= 500;
      throw error;
    }
  }

  /**
   * A 401 survived refresh + cookie-settle retries. Before tearing down the
   * session (which also throws away the user's unsaved work — e.g. a note
   * autosave), POSITIVELY confirm the session is actually dead.
   *
   * WHY: on a mobile resume the browser can keep the freshly-refreshed access
   * cookie mid-commit — it hasn't attached it to the immediate retry yet — while
   * the session is perfectly alive on the server. In exactly this scenario the
   * WebSocket-token endpoint (which ALSO requires authentication) keeps
   * returning 200. Logging out here is a false positive: a live session and the
   * user's draft are discarded over a cookie-commit race.
   *
   * We probe with one coordinated refresh (deduped app-wide, so it can't race
   * the rotating /auth/refresh/ endpoint):
   *   - refresh ok            → session/refresh-token valid → NOT a real failure
   *   - explicit 401 / 403    → session genuinely rejected  → real auth failure
   *   - network / 5xx / error → transient                   → keep the session
   * Returns true only when the session is genuinely dead and a logout is due.
   */
  private async confirmAuthFailure(): Promise<boolean> {
    try {
      const result = await coordinatedRefresh();
      if (result.ok) return false;
      return result.status === 401 || result.status === 403;
    } catch {
      return false;
    }
  }

  // Enhanced authentication with backend health check and retry logic
  async silentAuthRequest<T>(url: string, data?: any, retryCount = 0): Promise<T> {
    const fullUrl = `${API_BASE_URL}/${url.replace(/^\//, '')}`;
    const maxRetries = 2;

    if (DEBUG_LOGS) {
      console.log('[AUTH] Silent auth request to:', fullUrl, `(attempt ${retryCount + 1}/${maxRetries + 1})`);
      console.log('[AUTH] Refresh token cookie visibility in JS is unreliable (HttpOnly). Proceeding with credentials include.');
    }

    // CSRF: get token from cookie, or for login endpoints fetch from backend first (never send empty header)
    let csrfToken = this.getCSRFToken();
    const isLoginStep = url.includes('login-step1') || url.includes('login-step2');
    if (isLoginStep && (!csrfToken || csrfToken.length < 32)) {
      try {
        const csrfUrl = `${API_BASE_URL}/auth/csrf/`;
        const csrfRes = await fetch(csrfUrl, { method: 'GET', credentials: 'include', signal: createTimeoutSignal(5000) });
        if (csrfRes.ok) {
          const csrfJson = await csrfRes.json();
          if (csrfJson?.csrfToken && typeof csrfJson.csrfToken === 'string' && csrfJson.csrfToken.length >= 32) {
            csrfToken = csrfJson.csrfToken;
          }
        }
      } catch (_) {
        // keep existing csrfToken (may still be null)
      }
    }

    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (csrfToken && csrfToken.length >= 32) {
      headers['X-CSRFToken'] = csrfToken;
    }

    try {
      const response = await fetch(fullUrl, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: data ? JSON.stringify(data) : undefined,
        signal: createTimeoutSignal(10000), // 10 second timeout with legacy Edge fallback
      });

      if (DEBUG_LOGS) {
        console.log('[AUTH] Refresh response status:', response.status);
      }
      
      // Handle 502/503 as backend temporarily down - retry with exponential backoff
      if ((response.status === 502 || response.status === 503 || response.status === 521) && retryCount < maxRetries) {
        const backoffMs = Math.pow(2, retryCount) * 1000; // 1s, 2s, 4s
        if (DEBUG_LOGS) {
          console.log(`[AUTH] ⚠️ Backend temporarily unavailable (${response.status}), retrying in ${backoffMs}ms...`);
        }
        await new Promise(resolve => setTimeout(resolve, backoffMs));
        return this.silentAuthRequest<T>(url, data, retryCount + 1);
      }

      const responseData = await response.json();
      if (DEBUG_LOGS) {
        console.log('[AUTH] Refresh response data:', responseData);
      }
      
      if (response.ok) {
        if (DEBUG_LOGS) {
          console.log('[AUTH] ✅ Session refreshed successfully');
        }
        return responseData;
      } else {
        const error = new Error(`HTTP ${response.status}`);
        (error as any).response = {
          status: response.status,
          data: responseData,
          statusText: response.statusText
        };
        throw error;
      }
    } catch (error: any) {
      // Distinguish between network errors and auth failures
      if (error.name === 'AbortError' || error.name === 'TimeoutError') {
        if (retryCount < maxRetries) {
          const backoffMs = Math.pow(2, retryCount) * 1000;
          if (DEBUG_LOGS) {
            console.log(`[AUTH] ⚠️ Request timeout, retrying in ${backoffMs}ms...`);
          }
          await new Promise(resolve => setTimeout(resolve, backoffMs));
          return this.silentAuthRequest<T>(url, data, retryCount + 1);
        }
      }
      
      if (DEBUG_LOGS) {
        console.error('[AUTH] ❌ Request failed:', error.message);
      }
      throw error;
    }
  }

  constructor() {
    if (DEBUG_VERBOSE_API) {
      console.log('[ApiService] Creating axios instance with baseURL:', API_BASE_URL);
    }
    this.api = axios.create({
      baseURL: API_BASE_URL,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true, // CRITICAL: Required for cookies to be sent/received
      // Disable axios automatic console error logging for cleaner output
      validateStatus: (status) => status < 500, // Accept all status codes < 500 as valid
      // Transform response to handle empty or malformed JSON
      transformResponse: [
        function (data) {
          // Handle empty, null, or undefined responses
          if (!data || data === '' || data === 'null' || data === 'undefined' || data === '"undefined"') {
            return {};
          }
          // If data is already an object, return it
          if (typeof data === 'object') {
            return data;
          }
          // Try to parse as JSON
          try {
            return JSON.parse(data);
          } catch (e) {
            // If parsing fails, return as error object
            return { error: data };
          }
        }
      ]
    });

    this.setupInterceptors();
    this.setupActivityTracking();
  }

  /**
   * Counts in-flight requests for services/apiActivity.ts (which the boot
   * loading screen uses to tell when the current screen has stopped fetching).
   *
   * Registered AFTER setupInterceptors() on purpose. Axios runs response
   * handlers in registration order, so ours runs LAST — after every
   * refresh-and-retry path above has settled. Registering it first would let
   * the counter drop to zero in the middle of a token refresh, and that gap
   * would read as "everything finished loading".
   */
  private setupActivityTracking() {
    this.api.interceptors.request.use(
      (config) => {
        beginApiRequest();
        return config;
      },
      (error) => Promise.reject(error)
    );

    this.api.interceptors.response.use(
      (response) => {
        endApiRequest();
        return response;
      },
      (error) => {
        endApiRequest();
        return Promise.reject(error);
      }
    );
  }

  private setupInterceptors() {
    // Request interceptor - cookies are automatically included, no manual token injection needed
    this.api.interceptors.request.use(
      (config) => {
        // If data is FormData, remove Content-Type header to let browser set it with boundary
        if (config.data instanceof FormData) {
          delete config.headers['Content-Type'];
        }

        // Endpoints whose work legitimately outlasts the default timeout get
        // theirs raised here. See SLOW_ENDPOINTS for the table and the reasons.
        applySlowEndpointTimeout(config);

        if (DEBUG_VERBOSE_API) {
          console.log('[REQUEST] URL:', config.url);
          console.log('[REQUEST] Cookies available:', document.cookie || 'NONE');
          console.log('[REQUEST] Headers:', config.headers);
        }

        // Ensure cookies are included in requests
        config.withCredentials = true;
        
        // Add CSRF token if available (for POST, PUT, DELETE requests)
        // Temporarily disable CSRF for debugging
        if (['post', 'put', 'patch', 'delete'].includes(config.method?.toLowerCase() || '')) {
          const csrfToken = this.getCSRFToken();
          if (csrfToken) {
            config.headers['X-CSRFToken'] = csrfToken;
          }
          // Add debug headers
          if (DEBUG_VERBOSE_API) {
            console.log('[DEBUG] CSRF Token:', csrfToken);
          }
        }
        
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor with automatic token refresh
    this.api.interceptors.response.use(
      async (response: AxiosResponse) => {
        // Suppress logging for 404 errors on message status endpoint (expected for new messages)
        const isMessageStatus404 = response.status === 404 && 
                                   response.config?.url?.includes('/messages/') && 
                                   response.config?.url?.includes('/status/');
        
        if (DEBUG_VERBOSE_API && !isMessageStatus404) {
          console.log('[AXIOS DEBUG] Response interceptor - success:', {
            url: response.config?.url,
            status: response.status
          });
        }

        // Handle 412 Precondition Failed with code=disclaimer_required.
        // Emitted by accounts.permissions.HasAcceptedCurrentDisclaimer when
        // the authenticated user has not yet accepted the current
        // confidentiality disclaimer for their active hospital. Notify the
        // DisclaimerGate so it can re-check + present the modal, and reject
        // the original request so the caller does not silently proceed.
        if (response.status === 412) {
          const data = (response.data || {}) as {
            code?: string;
            hospital_id?: number | null;
            version?: number | null;
            compliance_mode?: 'USER_RESPONSIBILITY' | 'FULL_HIPAA' | 'DISABLED' | null;
          };
          if (data.code === 'disclaimer_required') {
            if (DEBUG_LOGS) {
              console.warn('[DISCLAIMER] 412 disclaimer_required from', response.config?.url, data);
            }
            notifyDisclaimerRequired({
              hospital_id: data.hospital_id ?? null,
              version: data.version ?? null,
              compliance_mode: data.compliance_mode ?? null,
            });
            return Promise.reject({
              response,
              config: response.config,
              message: 'Disclaimer acknowledgment required',
            });
          }
        }

        // Handle 401 even in success case (due to validateStatus accepting < 500)
        const config = response.config as ExtendedAxiosRequestConfig;
        if (response.status === 401 && !config._retry) {
          // _skipAuthFailure means "do not trigger global logout/redirect for
          // this request" (used by background polling like
          // /feature-enablement/my-features/, /notifications/summary/,
          // /notifications/). It MUST still attempt a refresh-and-retry so a
          // routinely-expired access cookie auto-recovers; otherwise polled
          // endpoints stay 401 forever even after a user-initiated request
          // refreshes the cookie. The "skip" only applies to the
          // handleAuthFailure() call in the catch-block below.
          if (!this.shouldAttemptSessionRefresh()) {
            if (DEBUG_LOGS) {
              console.log('[AUTH] 401 during non-authenticated flow - skipping refresh attempt');
            }
            return Promise.reject({
              response,
              config: config,
              message: 'Authentication required'
            });
          }

          if (DEBUG_LOGS) {
            console.log('[AUTH] 401 detected in success interceptor, attempting token refresh...');
          }
          config._retry = true;

          try {
            await this.refreshSession();
            if (DEBUG_LOGS) {
              console.log('[AUTH] Token refresh successful, retrying request');
              console.log('[AUTH] Cookies after refresh:', document.cookie);
              console.log('[AUTH] Auth token now:', document.cookie.includes('hospital_auth_token') ? 'EXISTS' : 'STILL MISSING');
            }
            return this.api.request(config);
          } catch (refreshError) {
            const reason = this.getAuthFailureReason(refreshError);
            if (DEBUG_LOGS) {
              console.error('[AUTH] Token refresh failed:', reason);
            }
            // Background polls fail silently (no global logout). Foreground
            // requests trigger handleAuthFailure → redirect to login — but ONLY
            // on an explicit auth rejection. A network/5xx refresh failure means
            // we never learned whether the session is valid, and logging out on
            // it discards a live session (and the user's unsaved work) over a
            // dropped packet. This guard was previously supplied by accident:
            // refreshCoordinator retried network errors, so the throw rarely
            // reached here. That retry is gone (it re-presented a rotating
            // token), which makes the check explicit rather than incidental.
            if (
              !(refreshError as any)?.isTransientRefreshFailure &&
              !config._skipAuthFailure &&
              this.shouldAttemptSessionRefresh()
            ) {
              this.handleAuthFailure(reason);
            }
            return Promise.reject({
              response,
              config: config,
              message: reason
            });
          }
        }

        if (response.status === 401) {
          if (config._skipAuthFailure) {
            return Promise.reject({ response, config, message: 'Unauthorized' });
          }

          // Reaching here means the refresh-and-retry above already ran and the
          // retried request STILL got a 401. On mobile this is frequently a
          // cookie-commit race, not a dead session: the refresh succeeded and
          // sent a new access-token cookie, but the browser hadn't committed it
          // to the cookie jar before the immediate retry fired (seen as repeated
          // /api/notes/* 401s right after a successful refresh). Before logging
          // the user out, retry a few times with growing backoff so the cookie
          // can settle. A single 350ms retry proved too short on some devices —
          // the note-save 401 lost the race and forced a spurious logout while
          // the session was still alive (the WebSocket-token endpoint kept
          // returning 200). Bounded by _cookieSettleAttempts so it can never loop.
          const MAX_COOKIE_SETTLE_ATTEMPTS = 3;
          const settleAttempts = config._cookieSettleAttempts ?? 0;
          if (
            settleAttempts < MAX_COOKIE_SETTLE_ATTEMPTS &&
            this.shouldAttemptSessionRefresh()
          ) {
            config._cookieSettleAttempts = settleAttempts + 1;
            const delayMs = 300 * Math.pow(2, settleAttempts); // 300ms, 600ms, 1200ms
            await new Promise((resolve) => setTimeout(resolve, delayMs));
            if (DEBUG_LOGS) {
              console.log(
                `[AUTH] Post-refresh 401 — cookie-settle retry ` +
                `${config._cookieSettleAttempts}/${MAX_COOKIE_SETTLE_ATTEMPTS} after ${delayMs}ms`,
                response.config?.url
              );
            }
            return this.api.request(config);
          }

          const reason = this.getAuthFailureReason(
            { response },
            'Your session has expired or is invalid. Please log in again.'
          );

          // The 401 survived refresh + settle retries. Do NOT assume the session
          // is dead — confirm it first (see confirmAuthFailure). A cookie-commit
          // race keeps the session alive on the server while this request can't
          // present the freshly-refreshed cookie yet; logging out here would
          // discard a live session and the user's unsaved work. Only an explicit
          // auth rejection from the probe triggers logout; otherwise reject
          // transiently so callers (e.g. note autosave) keep their draft and retry.
          if (this.shouldAttemptSessionRefresh()) {
            const sessionDead = await this.confirmAuthFailure();
            if (sessionDead) {
              this.handleAuthFailure(reason);
            } else if (DEBUG_LOGS) {
              console.log(
                '[AUTH] Surviving 401 but session still valid — not logging out',
                response.config?.url
              );
            }
          }

          return Promise.reject({
            response,
            config,
            message: reason
          });
        }

        // Extend last activity on successful authenticated API calls so "working" (API use) resets idle timer
        const url = String(response.config?.url ?? '');
        const isPublicEndpoint = /auth\/(login|refresh|csrf|session-status)/.test(url);
        if (response.status >= 200 && response.status < 300 && !isPublicEndpoint) {
          StorageService.updateLastActivity();
        }

        return response;
      },
      async (error) => {
        // Suppress console errors for authentication endpoints and message status 404s
        const isAuthEndpoint = error.config?.url?.includes('auth/login') || error.config?.url?.includes('auth/refresh');
        const isMessageStatus404 = error.response?.status === 404 && 
                                   error.config?.url?.includes('/messages/') && 
                                   error.config?.url?.includes('/status/');
        
        // Ensure error.response exists (create if missing for network errors)
        if (!error.response) {
          error.response = {
            status: 0,
            statusText: 'Network Error',
            data: { error: error.message || 'Network error occurred' },
            headers: {},
            config: error.config
          };
        }
        
        // Handle cases where response data might be undefined or invalid JSON
        if (error.response) {
          // Ensure response.data exists
          if (error.response.data === undefined) {
            error.response.data = {};
          } else if (typeof error.response.data === 'string') {
            // Handle string responses that might be invalid JSON
            if (error.response.data.trim() === '' || 
                error.response.data === 'null' || 
                error.response.data === 'undefined' ||
                error.response.data === '"undefined"') {
              // Empty string, null, undefined, or "undefined" string - set to empty object
              error.response.data = {};
            } else {
              // Try to parse as JSON if it looks like JSON
              try {
                if (error.response.data.trim().startsWith('{') || error.response.data.trim().startsWith('[')) {
                  error.response.data = JSON.parse(error.response.data);
                }
              } catch (parseError) {
                // If parsing fails, wrap in error object
                error.response.data = { error: error.response.data };
              }
            }
          } else if (error.response.data === null || error.response.data === 'null' || error.response.data === 'undefined') {
            // Null or undefined - set to empty object
            error.response.data = {};
          }
          
          // Ensure proper error message for 403 (permission denied)
          if (error.response.status === 403 && (!error.response.data || Object.keys(error.response.data).length === 0)) {
            error.response.data = { 
              error: 'Permission denied. You do not have write access to this resource.',
              detail: 'You need write access to perform this action.'
            };
          }
        }
        
        if (!isAuthEndpoint && !isMessageStatus404 && DEBUG_VERBOSE_API) {
          console.log('[AXIOS DEBUG] Response interceptor - error:', {
            status: error.response?.status,
            url: error.config?.url,
            message: error.message,
            error: error
          });
        }
        
        // For message status 404s, return a default response instead of throwing
        if (isMessageStatus404) {
          return Promise.resolve({
            data: {
              total_recipients: 0,
              acknowledged_count: 0,
              delivered_count: 0,
              read_count: 0,
              pending_count: 0,
              not_found: true
            },
            status: 200,
            statusText: 'OK',
            headers: {},
            config: error.config
          } as AxiosResponse);
        }
        
        // Handle network errors (connection lost)
        if (!error.response && error.code === 'ERR_NETWORK') {
          // For long-running operations, check if this might be a timeout during processing
          const isLongRunningOp = error.config?.url?.includes('data-analysis') && 
                                  (error.config?.url?.includes('/data/load/') ||
                                   error.config?.url?.includes('/operations/apply-operations/') ||
                                   error.config?.url?.includes('/operations/validate/') ||
                                   error.config?.url?.includes('/data/download-processed/'));
          
          if (isLongRunningOp && error.code === 'ECONNABORTED') {
            // This is likely a timeout during processing, not a disconnection
            // Don't mark as disconnected - the operation may still be running
            if (DEBUG_LOGS) {
              console.log('[API] Timeout during long-running operation - server may still be processing');
            }
          } else {
            // True network error - mark as disconnected
            if (window.store && window.store.dispatch) {
              window.store.dispatch({ 
                type: 'connection/setDisconnected', 
                payload: { error: 'Network connection lost' } 
              });
            }
          }
        }
        
        // Handle timeout errors - distinguish between timeout during processing vs disconnection
        if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
          const isLongRunningOp = error.config?.url?.includes('data-analysis') && 
                                  (error.config?.url?.includes('/data/load/') ||
                                   error.config?.url?.includes('/operations/apply-operations/') ||
                                   error.config?.url?.includes('/operations/validate/') ||
                                   error.config?.url?.includes('/data/download-processed/'));
          
          if (isLongRunningOp) {
            // Enhanced error for long-running operations
            const enhancedError: any = new Error('Operation is taking longer than expected. The server may still be processing your request in the background.');
            enhancedError.code = 'PROCESSING_TIMEOUT';
            enhancedError.isProcessingTimeout = true;
            enhancedError.originalError = error;
            return Promise.reject(enhancedError);
          }
        }
        
        const originalRequest = error.config as ExtendedAxiosRequestConfig;

        // _skipAuthFailure means "do not trigger global logout for this
        // request" (background polling). It MUST still flow through
        // refresh-and-retry so polled endpoints recover after a routine
        // access-cookie expiry. The "skip" is enforced INSIDE the catch
        // block below — it suppresses handleAuthFailure(), not the refresh.
        // Previously this short-circuited at the top of the error handler,
        // which left polled calls (feature-enablement, notifications) stuck
        // at 401 forever even when a refresh would have succeeded.

        // Check if backend cleared expired cookies
        if (error.response?.headers?.['x-auth-cookies-cleared'] === 'true') {
          console.warn(
            `[LOGOUT-DEBUG] api.ts.cookie_cleared_401 | reason=backend_cleared_expired_cookies | ` +
            `requestUrl=${error.config?.url} | status=${error.response?.status} | ` +
            `timestamp=${new Date().toISOString()}`
          );
          // Clear any local auth state.
          // NOTE: previously dispatched `{ type: 'auth/logout' }` which was a
          // no-op (no such reducer; logout is the `logoutAsync` thunk). The
          // real state-clearing is done by handleAuthFailure() → clearAuth.
          // Don't attempt refresh, go straight to login
          this.handleAuthFailure('Your previous session has expired. Please log in again.');
          return Promise.reject(error);
        }

        if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
          if (!this.shouldAttemptSessionRefresh()) {
            if (DEBUG_LOGS) {
              console.log('[AUTH] 401 during unauthenticated flow - suppressing refresh attempt');
            }
            return Promise.reject(error);
          }

          originalRequest._retry = true;

          try {
            // Use silent auth request for token refresh to avoid console errors
            await this.refreshSession();

            // Token refreshed successfully, retry original request
            if (DEBUG_LOGS) {
              console.log('🔄 Token refreshed, retrying request');
            }
            return this.api.request(originalRequest);
          } catch (refreshError: any) {
            // Determine failure reason for better user feedback
            let reason = 'Authentication verification failed.';
            if (refreshError.message?.includes('Refresh token missing')) {
              reason = 'Your session has expired. Please log in again.';
            } else if (refreshError.message?.includes('timeout') || refreshError.name === 'AbortError') {
              reason = 'Server connection timeout. Please try again.';
            }

            if (DEBUG_LOGS) {
              const errorMsg = refreshError instanceof Error
                ? refreshError.message
                : (refreshError as any)?.response?.data?.error
                || (refreshError as any)?.message
                || JSON.stringify(refreshError, null, 2);
              console.error('[AUTH] Token refresh failed:', errorMsg);
            }
            console.warn(
              `[LOGOUT-DEBUG] api.ts.401_refresh_failed | reason=${reason} | ` +
              `requestUrl=${originalRequest?.url} | status=401 | ` +
              `skipAuthFailure=${!!originalRequest?._skipAuthFailure} | ` +
              `timestamp=${new Date().toISOString()}`
            );
            // Background polls fail silently (no global logout). Foreground
            // requests trigger handleAuthFailure → redirect to login — but only
            // on an explicit auth rejection, never on a network/5xx refresh
            // failure (playbook §10; same reasoning as the success-interceptor
            // branch above).
            if (
              !(refreshError as any)?.isTransientRefreshFailure &&
              !originalRequest?._skipAuthFailure
            ) {
              this.handleAuthFailure(reason);
            }
            // CRITICAL: return here so we don't fall through into the
            // "401 final fallback" block below which would call
            // handleAuthFailure a second time for the same error.
            // LogoutCoordinator gates duplicate calls, but the wasted control
            // flow + log noise contributed to debugging the 401-storm loop.
            return Promise.reject(error);
          }
        }

        if (error.response?.status === 401) {
          if (originalRequest?._skipAuthFailure) {
            return Promise.reject(error);
          }
          if (this.shouldAttemptSessionRefresh()) {
            const reason = error.response?.data?.error || 'Your session has expired or is invalid. Please log in again.';
            // Same guard as the success-interceptor path: confirm the session is
            // genuinely dead before logging out, so a cookie-commit race after a
            // mobile resume can't force a spurious logout on a live session.
            const sessionDead = await this.confirmAuthFailure();
            if (sessionDead) {
              console.warn(
                `[LOGOUT-DEBUG] api.ts.401_final_fallback | reason=${reason} | ` +
                `requestUrl=${originalRequest?.url} | status=401 | timestamp=${new Date().toISOString()}`
              );
              this.handleAuthFailure(reason);
            } else if (DEBUG_LOGS) {
              console.log(
                '[AUTH] 401 final fallback but session still valid — not logging out',
                originalRequest?.url
              );
            }
          } else {
            if (DEBUG_LOGS) {
              console.log('[AUTH] 401 during unauthenticated flow - no forced logout');
            }
          }
        }

        return Promise.reject(error);
      }
    );
  }
  
  private getCSRFToken(): string | null {
    // Extract CSRF token from cookie for CSRF protection
    const name = 'csrftoken';
    let cookieValue = null;
    if (document.cookie && document.cookie !== '') {
      const cookies = document.cookie.split(';');
      for (let i = 0; i < cookies.length; i++) {
        const cookie = cookies[i].trim();
        if (cookie.substring(0, name.length + 1) === (name + '=')) {
          cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
          break;
        }
      }
    }
    return cookieValue;
  }
  
  private handleAuthFailure(reason?: string): void {
    // Prevent concurrent logout attempts from multiple interceptors
    if (!LogoutCoordinator.requestLogout(reason || 'unknown', 'api.ts')) {
      return;
    }

    console.warn(
      `[LOGOUT-DEBUG] api.ts.handleAuthFailure | reason=${reason || 'unknown'} | ` +
      `currentUrl=${window.location.href} | timestamp=${new Date().toISOString()}`
    );

    // Clear only localStorage data, cookies cleared by server
    localStorage.removeItem('hospital_user');
    localStorage.removeItem('hospital_token_expiry');

    // Clear Redux authentication state and trigger soft redirect via React Router
    if (window.store && window.store.dispatch) {
      window.store.dispatch({ type: 'auth/clearAuth' });

      // Use soft redirect via Redux to avoid destroying React state
      const currentPath = window.location.pathname;
      const isLoginPage = currentPath === '/login' || currentPath === '/onecare/login' || currentPath === '/onecare/login-legacy';
      const isLandingPage = currentPath === '/' || currentPath === '/onecare' || currentPath === '/onecare/';
      const isPatientPage = currentPath.startsWith('/patients') || currentPath.startsWith('/onecare/patients');

      if (!isLoginPage && !isLandingPage && !isPatientPage) {
        const loginPath = currentPath.startsWith('/onecare') ? '/onecare/login' : '/login';
        const errorMsg = reason || 'Your session has expired. Please log in again.';
        window.store.dispatch({
          type: 'auth/setPendingLogoutRedirect',
          payload: { path: `${loginPath}?redirect=${encodeURIComponent(currentPath)}`, error: errorMsg }
        });
      }
    } else {
      // Fallback: hard redirect if store not available
      const currentPath = window.location.pathname;
      const isLoginPage = currentPath === '/login' || currentPath === '/onecare/login' || currentPath === '/onecare/login-legacy';
      const isLandingPage = currentPath === '/' || currentPath === '/onecare' || currentPath === '/onecare/';
      const isPatientPage = currentPath.startsWith('/patients') || currentPath.startsWith('/onecare/patients');

      if (!isLoginPage && !isLandingPage && !isPatientPage) {
        const loginPath = currentPath.startsWith('/onecare') ? '/onecare/login' : '/login';
        const redirectUrl = reason
          ? `${loginPath}?error=${encodeURIComponent(reason)}&redirect=${encodeURIComponent(currentPath)}`
          : `${loginPath}?redirect=${encodeURIComponent(currentPath)}`;
        window.location.href = redirectUrl;
      }
    }
  }

  async get<T>(url: string, config?: any): Promise<T> {
    if (DEBUG_VERBOSE_API) {
      console.log('[API DEBUG] Making GET request to:', url);
    }
    try {
      const response = await this.api.get<T>(url, config);
      
      // Suppress logging for 404 errors on message status endpoint (expected for new messages)
      const isMessageStatus404 = response.status === 404 && url.includes('/messages/') && url.includes('/status/');
      
      if (DEBUG_VERBOSE_API && !isMessageStatus404) {
        console.log('[API DEBUG] GET Response:', { status: response?.status, url });
      }
      
      // For 404 on message status, return default data instead of throwing
      if (isMessageStatus404) {
        return {
          total_recipients: 0,
          acknowledged_count: 0,
          delivered_count: 0,
          read_count: 0,
          pending_count: 0,
          not_found: true
        } as T;
      }
      
      return response.data;
    } catch (error: any) {
      // Suppress 404 errors for message status endpoint
      const isMessageStatus404 = error?.response?.status === 404 && url.includes('/messages/') && url.includes('/status/');
      
      if (isMessageStatus404) {
        // Return default summary for 404 on message status
        return {
          total_recipients: 0,
          acknowledged_count: 0,
          delivered_count: 0,
          read_count: 0,
          pending_count: 0,
          not_found: true
        } as T;
      }
      
      if (DEBUG_LOGS) {
        console.error('[API DEBUG] GET Error:', error);
      }
      throw error;
    }
  }

  async post<T>(url: string, data?: any, config?: any): Promise<T> {
    const isAuthEndpoint =
      url.includes('auth/login') ||
      url.includes('auth/refresh') ||
      url.includes('auth/switch-hospital');
    
    // Use silent auth request for authentication endpoints to avoid console errors
    if (isAuthEndpoint) {
      return this.silentAuthRequest<T>(url, data);
    }
    
    try {
      const response = await this.api.post<T>(url, data, config);
      
      // Handle successful responses that might have error status codes
      if (response.status >= 400) {
        console.error('[API] HTTP Error Response:', {
          status: response.status,
          statusText: response.statusText,
          data: response.data,
          url: url,
          requestData: url.includes('endoscopy-discharge') ? '[not logged]' : data
        });
        const error = new Error(`HTTP ${response.status}`);
        (error as any).response = response;
        throw error;
      }
      
      // Handle empty or undefined response data
      if (response.data === undefined || response.data === null) {
        console.warn('[API] Empty response data for POST request:', url);
        // Return empty object for successful requests with no data
        return {} as T;
      }
      
      return response.data;
    } catch (error: any) {
      // Ensure error.response exists (handle network errors and JSON parsing errors)
      if (!error.response) {
        error.response = {
          status: 0,
          statusText: 'Network Error',
          data: { error: error.message || 'Network error occurred' },
          headers: {},
          config: error.config
        };
      }
      
      // Handle JSON parsing errors specifically
      if (error.message && (error.message.includes('JSON') || error.message.includes('parse') || error.message.includes('undefined'))) {
        if (!error.response.data || Object.keys(error.response.data).length === 0) {
          error.response.data = {
            error: 'Invalid response from server',
            detail: 'The server returned an invalid response. Please try again or contact support.'
          };
        }
      }
      
      // Log non-auth errors when debug logging is enabled
      if (DEBUG_LOGS) {
        const errorMessage = error instanceof Error 
          ? error.message 
          : (error as any)?.response?.data?.error 
          || (error as any)?.message 
          || JSON.stringify(error, null, 2);
        console.error('[API DEBUG] POST Error:', errorMessage);
      }
      throw error;
    }
  }

  async put<T>(url: string, data?: any, config?: any): Promise<T> {
    const response = await this.api.put<T>(url, data, config);
    return response.data;
  }

  async patch<T>(url: string, data?: any, config?: any): Promise<T> {
    try {
      const response = await this.api.patch<T>(url, data, config);
      
      // Handle successful responses that might have error status codes
      if (response.status >= 400) {
        console.error('[API] HTTP Error Response:', {
          status: response.status,
          statusText: response.statusText,
          data: response.data,
          url: url,
          requestData: url.includes('endoscopy-discharge') ? '[not logged]' : data
        });
        const error = new Error(`HTTP ${response.status}`);
        (error as any).response = response;
        throw error;
      }
      return response.data;
    } catch (error) {
      // Log errors when debug logging is enabled
      if (DEBUG_LOGS) {
        const errorMessage = error instanceof Error 
          ? error.message 
          : (error as any)?.response?.data?.error 
          || (error as any)?.message 
          || JSON.stringify(error, null, 2);
        console.error('[API DEBUG] PATCH Error:', errorMessage);
      }
      throw error;
    }
  }

  async delete<T>(url: string, data?: any, config?: any): Promise<T> {
    const response = await this.api.delete<T>(url, { data, ...config });
    return response.data;
  }

  async downloadBlob(url: string, config?: any): Promise<Blob> {
    if (DEBUG_LOGS) {
      console.log('[CSV Export] Downloading blob from:', url);
      console.log('[CSV Export] Auth token cookie:', document.cookie.includes('hospital_auth_token') ? 'EXISTS' : 'MISSING');
      console.log('[CSV Export] All cookies:', document.cookie);
    }

    try {
      // Extract params and timeout from config
      // Config can be either { params: {...}, timeout: ... } or just { timeout: ... }
      const params = config?.params;
      const timeout = config?.timeout;
      
      const requestConfig: any = {
        responseType: 'blob'
      };
      
      if (params) {
        requestConfig.params = params;
      }
      
      if (timeout) {
        requestConfig.timeout = timeout;
      }
      
      const response = await this.api.get(url, requestConfig);

      if (DEBUG_LOGS) {
        console.log('[API DEBUG] Blob response:', {
          status: response.status,
          size: response.data.size,
          type: response.data.type
        });
      }

      // Check if the blob is actually an error response (JSON in blob format)
      if (response.data.type === 'application/json') {
        const text = await response.data.text();
        const error = JSON.parse(text);
        throw new Error(error.error || error.message || 'Download failed');
      }

      return response.data;
    } catch (error: any) {
      // If response has blob data that's actually JSON, try to extract the error
      if (error.response && error.response.data instanceof Blob && error.response.data.type === 'application/json') {
        const text = await error.response.data.text();
        try {
          const errorData = JSON.parse(text);
          throw new Error(errorData.error || errorData.message || 'Download failed');
        } catch {
          // If parsing fails, just throw the original error
        }
      }
      throw error;
    }
  }

  // Messaging API methods
  async getConversations(): Promise<any[]> {
    if (DEBUG_LOGS) {
      console.log('[API] 📡 Fetching conversations...');
    }
    const result = await this.get('/messages/conversations/') as any[];
    if (DEBUG_LOGS) {
      console.log('[API] ✅ Conversations fetched:', {
        count: result.length,
        conversations: result.map((c: any) => ({ id: c.id, name: c.name, type: c.type }))
      });
    }
    return result;
  }

  async getMessages(
    conversationId?: string,
    search?: string,
    options?: { range?: '24h' | 'all'; before?: string }
  ): Promise<any> {
    const params = new URLSearchParams();
    if (conversationId) params.append('conversation_id', conversationId);
    if (search) params.append('search', search);
    if (options?.range) params.append('range', options.range);
    if (options?.before) params.append('before', options.before);
    
    const queryString = params.toString();
    const url = queryString ? `/messages/?${queryString}` : '/messages/';
    return this.get(url);
  }

  async sendMessage(data: { 
    content: string; 
    receiver_id?: number; 
    group_id?: number;
    hospital_id?: number;
    urgency_level?: 'routine' | 'urgent' | 'critical';
    urgency_reason?: string;
    // Legacy fields for backward compatibility
    is_urgent?: boolean;
    priority?: 'normal' | 'urgent' | 'critical';
    _traceId?: string; // Trace ID for message flow tracking
  }): Promise<any> {
    // Map legacy fields to new urgency structure if needed
    const requestData: any = { ...data };
    const traceId = requestData._traceId;
    
    // If legacy fields are used, convert to new structure
    if (data.is_urgent && !data.urgency_level) {
      requestData.urgency_level = 'urgent';
    }
    if (data.priority && !data.urgency_level) {
      switch (data.priority) {
        case 'urgent':
          requestData.urgency_level = 'urgent';
          break;
        case 'critical':
          requestData.urgency_level = 'critical';
          break;
        default:
          requestData.urgency_level = 'routine';
      }
    }
    
    if (DEBUG_LOGS) {
      console.log('📤 Sending message with urgency data:', requestData);
    }
    
    // Pass trace ID in both body and header for backend extraction
    const config: any = {};
    if (traceId) {
      config.headers = {
        'X-Trace-Id': traceId
      };
    }
    
    return this.post('/messages/send/', requestData, config);
  }

  async markMessageRead(messageId: number): Promise<any> {
    return this.post(`/messages/mark-read/${messageId}/`);
  }

  // Message Acknowledgment API methods
  async getMessageAcknowledgments(messageId: number, params?: any): Promise<any> {
    const queryString = params ? `?${new URLSearchParams(params).toString()}` : '';
    return this.get(`/messages/${messageId}/acknowledgments/${queryString}`);
  }

  async acknowledgeMessage(messageId: number, data?: { status?: 'delivered' | 'read'; focus_read?: boolean }): Promise<any> {
    return this.post(`/messages/${messageId}/acknowledge/`, data);
  }

  async getMessageStatusSummary(messageId: number): Promise<any> {
    try {
      // Use a custom config to suppress 404 errors from being logged
      const response = await this.api.get(`/messages/${messageId}/status/`, {
        validateStatus: (status) => {
          // Don't throw for 404 - we'll handle it gracefully
          return status < 500;
        }
      });
      
      // If 404, return default summary
      if (response.status === 404) {
        return {
          total_recipients: 0,
          acknowledged_count: 0,
          delivered_count: 0,
          read_count: 0,
          pending_count: 0,
          not_found: true
        };
      }
      
      return response.data;
    } catch (error: any) {
      // Handle 404 gracefully - message might not exist yet
      if (error?.response?.status === 404) {
        // Return a default summary structure instead of throwing
        return {
          total_recipients: 0,
          acknowledged_count: 0,
          delivered_count: 0,
          read_count: 0,
          pending_count: 0,
          not_found: true
        };
      }
      throw error; // Re-throw other errors
    }
  }

  async bulkAcknowledgeMessages(data: { message_ids: number[]; status?: 'delivered' | 'read' }): Promise<any> {
    return this.post('/messages/bulk-acknowledge/', data);
  }

  async getUserAcknowledgmentStats(): Promise<any> {
    return this.get('/messages/acknowledgments/stats/');
  }

  async acknowledgeMessageCritical(messageId: number, deviceInfo?: string): Promise<any> {
    return this.post(`/messages/${messageId}/acknowledge-critical/`, {
      device_info: deviceInfo || navigator.userAgent
    });
  }

  // User API methods
  async getCurrentUser(): Promise<any> {
    try {
      const response = await this.get<any>('/users/me/');
      return response;
    } catch (error) {
      const errorMsg = error instanceof Error 
        ? error.message 
        : (error as any)?.response?.data?.error 
        || JSON.stringify(error, null, 2);
      console.error('API getCurrentUser error:', errorMsg);
      throw error;
    }
  }

  async updateCurrentUserProfile(data: { first_name?: string; last_name?: string; email?: string; phone?: string }): Promise<any> {
    try {
      const response = await this.patch<any>('/users/me/', data);
      return response;
    } catch (error) {
      console.error('API updateCurrentUserProfile error:', error);
      throw error;
    }
  }

  async changePassword(data: { current_password: string; new_password: string }): Promise<any> {
    try {
      const response = await this.post<any>('/users/change_password/', data);
      return response;
    } catch (error) {
      const errorMsg = error instanceof Error 
        ? error.message 
        : (error as any)?.response?.data?.error 
        || JSON.stringify(error, null, 2);
      console.error('API changePassword error:', errorMsg);
      throw error;
    }
  }

  async getUsers(): Promise<any[]> {
    try {
      const response = await this.get<any>('/users/');
      if (DEBUG_LOGS) {
        console.log('API getUsers response:', response, 'Type:', typeof response);
      }
      
      // Always return an array to prevent filter errors
      if (Array.isArray(response)) {
        return response;
      } else if (typeof response === 'object' && response !== null) {
        const dataObj = response as any;
        if (dataObj.results && Array.isArray(dataObj.results)) {
          return dataObj.results;
        } else if (dataObj.data && Array.isArray(dataObj.data)) {
          return dataObj.data;
        }
      }
      
      if (DEBUG_LOGS) {
        console.warn('Unexpected users response format, returning empty array:', response);
      }
      return [];
    } catch (error) {
      if (DEBUG_LOGS) {
        console.error('Error fetching users:', error);
      }
      return [];
    }
  }

  // Group API methods
  async getGroups(): Promise<any[]> {
    try {
      const response = await this.get('/messages/groups/');
      if (DEBUG_LOGS) {
        console.log('API getGroups response:', response, 'Type:', typeof response);
      }
      
      // Always return an array to prevent filter errors
      if (Array.isArray(response)) {
        return response;
      } else if (typeof response === 'object' && response !== null) {
        const dataObj = response as any;
        if (dataObj.results && Array.isArray(dataObj.results)) {
          return dataObj.results;
        } else if (dataObj.data && Array.isArray(dataObj.data)) {
          return dataObj.data;
        }
      }
      
      if (DEBUG_LOGS) {
        console.warn('Unexpected groups response format, returning empty array:', response);
      }
      return [];
    } catch (error) {
      if (DEBUG_LOGS) {
        const errorMsg = error instanceof Error 
          ? error.message 
          : (error as any)?.response?.data?.error 
          || JSON.stringify(error, null, 2);
        console.error('Error fetching groups:', errorMsg);
      }
      return [];
    }
  }

  async createGroup(data: { name: string; description?: string; member_ids: number[] }): Promise<any> {
    return this.post('/messages/groups/create/', data);
  }

  // Role API methods
  async getRoleGroups(): Promise<any[]> {
    try {
      const response = await this.get<any>('/role-groups/');
      return Array.isArray(response) ? response : (response as any)?.results || [];
    } catch (error) {
      if (DEBUG_LOGS) {
        console.error('Error fetching role groups:', error);
      }
      return [];
    }
  }

  async getRoleSpecialties(): Promise<any[]> {
    try {
      const response = await this.get<any>('/role-specialties/');
      return Array.isArray(response) ? response : (response as any)?.results || [];
    } catch (error) {
      if (DEBUG_LOGS) {
        console.error('Error fetching role specialties:', error);
      }
      return [];
    }
  }

  async getDoctorRanks(): Promise<any[]> {
    try {
      const response = await this.get<any>('/ranks/');
      return Array.isArray(response) ? response : (response as any)?.results || [];
    } catch (error) {
      if (DEBUG_LOGS) {
        console.error('Error fetching doctor ranks:', error);
      }
      return [];
    }
  }

  async getRoleGroupUsers(roleGroupId: number): Promise<any[]> {
    try {
      const response = await this.get<any>(`/users/role-groups/${roleGroupId}/users/`);
      return Array.isArray(response) ? response : (response as any)?.results || [];
    } catch (error) {
      if (DEBUG_LOGS) {
        console.error('Error fetching role group users:', error);
      }
      return [];
    }
  }

  async getRoleSpecialtyUsers(roleSpecialtyId: number): Promise<any[]> {
    try {
      const response = await this.get<any>(`/users/role-specialties/${roleSpecialtyId}/users/`);
      return Array.isArray(response) ? response : (response as any)?.results || [];
    } catch (error) {
      if (DEBUG_LOGS) {
        console.error('Error fetching role specialty users:', error);
      }
      return [];
    }
  }

  async updateUserRole(userId: number, data: { role_specialty?: number; doctor_rank?: number }): Promise<any> {
    try {
      return await this.put(`/users/${userId}/`, data);
    } catch (error) {
      if (DEBUG_LOGS) {
        console.error('Error updating user role:', error);
      }
      throw error;
    }
  }

  // Unit API methods
  async getUnits(): Promise<any[]> {
    try {
      const response = await this.get<any>('/units/');
      return Array.isArray(response) ? response : (response as any)?.results || [];
    } catch (error) {
      if (DEBUG_LOGS) {
        console.error('Error fetching units:', error);
      }
      return [];
    }
  }

  async getUnit(unitId: number): Promise<any> {
    return this.get(`/units/${unitId}/`);
  }

  async createUnit(data: any): Promise<any> {
    return this.post('/units/', data);
  }

  async updateUnit(unitId: number, data: any): Promise<any> {
    return this.put(`/units/${unitId}/`, data);
  }

  async deleteUnit(unitId: number): Promise<any> {
    return this.delete(`/units/${unitId}/`);
  }

  async getUnitStaff(unitId: number): Promise<any> {
    return this.get(`/units/${unitId}/staff/`);
  }

  async getUnitCurrentShifts(unitId: number): Promise<any[]> {
    try {
      const response = await this.get<any>(`/units/${unitId}/current_shifts/`);
      return Array.isArray(response) ? response : (response as any)?.results || [];
    } catch (error) {
      if (DEBUG_LOGS) {
        console.error('Error fetching unit current shifts:', error);
      }
      return [];
    }
  }

  // Unit Type API methods (CSV configurable)
  async getUnitTypes(): Promise<any[]> {
    try {
      const response = await this.get<any>('/unit-types/');
      return Array.isArray(response) ? response : (response as any)?.results || [];
    } catch (error) {
      if (DEBUG_LOGS) {
        console.error('Error fetching unit types:', error);
      }
      return [];
    }
  }

  async getUnitType(unitTypeId: number): Promise<any> {
    return this.get(`/unit-types/${unitTypeId}/`);
  }

  async getUnitTypeUnits(unitTypeId: number): Promise<any[]> {
    try {
      const response = await this.get<any>(`/unit-types/${unitTypeId}/units/`);
      return Array.isArray(response) ? response : (response as any)?.results || [];
    } catch (error) {
      if (DEBUG_LOGS) {
        console.error('Error fetching unit type units:', error);
      }
      return [];
    }
  }

  // Hospital Configuration API methods (CSV configurable)
  async getHospitalConfigurations(): Promise<any[]> {
    try {
      const response = await this.get<any>('/hospital-config/');
      return Array.isArray(response) ? response : (response as any)?.results || [];
    } catch (error) {
      if (DEBUG_LOGS) {
        console.error('Error fetching hospital configurations:', error);
      }
      return [];
    }
  }

  async getDefaultHospitalConfiguration(): Promise<any> {
    try {
      return await this.get('/hospital-config/default/');
    } catch (error) {
      if (DEBUG_LOGS) {
        console.error('Error fetching default hospital configuration:', error);
      }
      return null;
    }
  }

  async getHospitalConfiguration(configId: number): Promise<any> {
    return this.get(`/hospital-config/${configId}/`);
  }

  // Shift API methods
  async getShifts(params?: any): Promise<any[]> {
    try {
      const queryString = params ? `?${new URLSearchParams(params).toString()}` : '';
      const response = await this.get<any>(`/shifts/${queryString}`);
      return Array.isArray(response) ? response : (response as any)?.results || [];
    } catch (error) {
      if (DEBUG_LOGS) {
        console.error('Error fetching shifts:', error);
      }
      return [];
    }
  }

  async getShift(shiftId: number): Promise<any> {
    return this.get(`/shifts/${shiftId}/`);
  }

  // Shift Scheduling Sequences API methods
  async getSequences(groupId: number): Promise<any[]> {
    try {
      const response = await this.get<any>(`/shift-scheduling/sequences/?group=${groupId}`);
      return Array.isArray(response) ? response : (response as any)?.results || [];
    } catch (error) {
      if (DEBUG_LOGS) {
        console.error('Error fetching sequences:', error);
      }
      return [];
    }
  }

  async updateShiftHours(groupId: number, hours: number, regenerateSequences: boolean = false): Promise<any> {
    return this.patch(`/shift-scheduling/groups/${groupId}/update-shift-hours/`, {
      shift_hours: hours,
      regenerate_sequences: regenerateSequences
    });
  }

  async bulkUpdateSequences(groupId: number, sequences: Array<{id: number, order: number, shift: string, priority: boolean}>): Promise<any> {
    return this.post('/shift-scheduling/sequences/bulk-update/', {
      group_id: groupId,
      sequences: sequences
    });
  }

  async addSequence(groupId: number, sequence: {order: number, shift: string, priority: boolean}): Promise<any> {
    return this.post('/shift-scheduling/sequences/', {
      group: groupId,
      order: sequence.order,
      shift: sequence.shift,
      priority: sequence.priority
    });
  }

  async deleteSequence(sequenceId: number): Promise<void> {
    return this.delete(`/shift-scheduling/sequences/${sequenceId}/`);
  }

  async autoGenerateSequences(groupId: number): Promise<any> {
    return this.post(`/shift-scheduling/groups/${groupId}/auto-sequences/`);
  }

  // Shift Scheduling Policy manager (admin) API methods
  async getShiftSchedulingManageableHospitals(): Promise<{
    hospitals: Array<{ id: number; name: string; code: string; organization: string | null }>;
    active_hospital_id: number | null;
  }> {
    return this.get('/shift-scheduling/admin/hospitals/');
  }

  async getShiftSchedulingPolicy(hospitalId?: number): Promise<any> {
    const suffix = hospitalId ? `?hospital=${hospitalId}` : '';
    return this.get(`/shift-scheduling/admin/policy/${suffix}`);
  }

  async updateShiftSchedulingPolicy(policy: Record<string, any>, hospitalId?: number): Promise<any> {
    const suffix = hospitalId ? `?hospital=${hospitalId}` : '';
    return this.put(`/shift-scheduling/admin/policy/${suffix}`, policy);
  }

  // Shift Scheduling Change Rules API methods
  async getChangeRules(groupId: number): Promise<any[]> {
    try {
      const response = await this.get<any>(`/shift-scheduling/change-rules/?group=${groupId}`);
      if (DEBUG_LOGS) {
        console.log('[API] getChangeRules response:', {
          isArray: Array.isArray(response),
          hasResults: !!(response as any)?.results,
          responseType: typeof response,
          responseKeys: response && typeof response === 'object' ? Object.keys(response) : 'N/A',
          responseLength: Array.isArray(response) ? response.length : (response as any)?.results?.length || 0
        });
      }
      
      const rules = Array.isArray(response) ? response : (response as any)?.results || [];
      if (DEBUG_LOGS) {
        console.log('[API] getChangeRules returning:', {
          count: rules.length,
          sample: rules.length > 0 ? rules[0] : null,
          allRules: rules
        });
      }
      
      return rules;
    } catch (error) {
      console.error('[API] Error fetching change rules:', error);
      return [];
    }
  }

  /**
   * Replace a group's forbidden shift changes in one call. Each item is
   * `"<previous>><next>:<post_off|regular>"`; an empty list clears every
   * restriction (rules are bans, so nothing listed = everything allowed).
   */
  async replaceForbiddenChangeRules(groupId: number, forbidden: string[]): Promise<void> {
    await this.post('/shift-scheduling/change-rules/replace_forbidden/', {
      group: groupId,
      forbidden,
    });
  }

  async createShift(data: any): Promise<any> {
    return this.post('/shifts/', data);
  }

  async updateShift(shiftId: number, data: any): Promise<any> {
    return this.put(`/shifts/${shiftId}/`, data);
  }

  async deleteShift(shiftId: number): Promise<any> {
    return this.delete(`/shifts/${shiftId}/`);
  }

  async getMyShifts(): Promise<any[]> {
    try {
      const response = await this.get<any>('/shifts/my_shifts/');
      return Array.isArray(response) ? response : (response as any)?.results || [];
    } catch (error) {
      if (DEBUG_LOGS) {
        console.error('Error fetching my shifts:', error);
      }
      return [];
    }
  }

  async getCurrentShifts(): Promise<any[]> {
    try {
      const response = await this.get<any>('/shifts/');
      return Array.isArray(response) ? response : (response as any)?.results || [];
    } catch (error) {
      if (DEBUG_LOGS) {
        console.error('Error fetching current shifts:', error);
      }
      return [];
    }
  }

  async startShift(shiftId: number): Promise<any> {
    return this.post(`/shifts/${shiftId}/start_shift/`);
  }

  async endShift(shiftId: number): Promise<any> {
    return this.post(`/shifts/${shiftId}/end_shift/`);
  }

  // Department API methods
  async getDepartments(): Promise<any[]> {
    try {
      const response = await this.get<any>('/departments/');
      return Array.isArray(response) ? response : (response as any)?.results || [];
    } catch (error) {
      if (DEBUG_LOGS) {
        console.error('Error fetching departments:', error);
      }
      return [];
    }
  }

  async getDepartment(departmentId: number): Promise<any> {
    return this.get(`/departments/${departmentId}/`);
  }

  async getDepartmentStaff(departmentId: number): Promise<any[]> {
    try {
      const response = await this.get<any>(`/departments/${departmentId}/staff/`);
      return Array.isArray(response) ? response : (response as any)?.results || [];
    } catch (error) {
      if (DEBUG_LOGS) {
        console.error('Error fetching department staff:', error);
      }
      return [];
    }
  }

  // Dashboard API methods
  async getDashboardStats(): Promise<any> {
    return this.get('/dashboard-stats/');
  }

  // AI Message Delegation API methods
  async delegateMessage(data: {
    recipients_query: string;
    message_content: string;
    message_type: string;
  }): Promise<any> {
    return this.post('/messages/ai-delegate/', data);
  }

  async confirmDelegation(data: {
    session_id: string;
    confirmed: boolean;
  }): Promise<any> {
    return this.post('/messages/delegation/confirm/', data);
  }

  async searchDelegationRecipients(query: string): Promise<any> {
    const params = new URLSearchParams({ query });
    return this.get(`/messages/delegation/recipients/?${params.toString()}`);
  }

  async getDelegationPermissions(): Promise<any> {
    return this.get('/messages/delegation/permissions/');
  }

  async getDelegationLogs(): Promise<any> {
    return this.get('/messages/delegation/logs/');
  }

  async getDelegationStats(days?: number): Promise<any> {
    const params = days ? new URLSearchParams({ days: days.toString() }) : '';
    const queryString = params.toString();
    const url = queryString ? `/messages/delegation/stats/?${queryString}` : '/messages/delegation/stats/';
    return this.get(url);
  }

  // Chat History API methods
  async getChatHistory(params?: {
    search?: string;
    category?: string;
    archived?: boolean;
    favorite?: boolean;
    limit?: number;
    offset?: number;
  }): Promise<any> {
    const queryString = params ? `?${new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([_, v]) => v !== undefined).map(([k, v]) => [k, String(v)]))
    ).toString()}` : '';
    return this.get(`/chatbot/history/${queryString}`);
  }

  async getChatHistoryDetail(sessionId: string, params?: {
    limit?: number;
    offset?: number;
  }): Promise<any> {
    const queryString = params ? `?${new URLSearchParams(
      Object.fromEntries(Object.entries(params).map(([k, v]) => [k, String(v)]))
    ).toString()}` : '';
    return this.get(`/chatbot/history/${sessionId}/${queryString}`);
  }

  async updateChatHistory(sessionId: string, data: {
    title?: string;
    summary?: string;
    category?: string;
    archived?: boolean;
    favorite?: boolean;
    tags?: string[];
  }): Promise<any> {
    return this.put(`/chatbot/history/${sessionId}/`, data);
  }

  async archiveChatHistory(sessionId: string): Promise<any> {
    return this.post(`/chatbot/history/${sessionId}/archive/`);
  }

  async unarchiveChatHistory(sessionId: string): Promise<any> {
    return this.post(`/chatbot/history/${sessionId}/unarchive/`);
  }

  async toggleChatHistoryFavorite(sessionId: string): Promise<any> {
    return this.post(`/chatbot/history/${sessionId}/favorite/`);
  }

  async deleteChatHistory(sessionId: string, data?: {
    reason?: string;
    notes?: string;
  }): Promise<any> {
    return this.delete(`/chatbot/history/${sessionId}/delete/`, data);
  }

  async bulkDeleteChatHistory(data: {
    session_ids: string[];
    reason?: string;
    notes?: string;
    permanent?: boolean;
  }): Promise<any> {
    return this.post('/chatbot/history/bulk-delete/', data);
  }

  async restoreChatHistory(sessionId: string): Promise<any> {
    return this.post(`/chatbot/history/${sessionId}/restore/`);
  }

  async getDeletedChatHistory(params?: {
    limit?: number;
    offset?: number;
  }): Promise<any> {
    const queryString = params ? `?${new URLSearchParams(
      Object.fromEntries(Object.entries(params).map(([k, v]) => [k, String(v)]))
    ).toString()}` : '';
    return this.get(`/chatbot/history/deleted/${queryString}`);
  }
}

export const apiService = new ApiService();
export default apiService;
